import React, {useEffect, useMemo} from "react"
import {Swiper, SwiperSlide} from 'swiper/react';
import {domToReact} from "html-react-parser";
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import JSON5 from 'json5'

const useSwiperFilterListener = ({swiperId, setCurrentFilter}) => {
  useEffect(() => {
    const listener = (e) => {
      if (e.detail?.id === swiperId) {
        setCurrentFilter({filter: e.detail?.filter})
      }
    }

    document.addEventListener("filter-swiper-js-children", listener, false)

    if (!window.filterSwiperJsChildren) {
      window.filterSwiperJsChildren = (id, filter) => {
        const event = new CustomEvent("filter-swiper-js-children", { detail: {
            id,
            filter,
          } })
        document.dispatchEvent(event)
      }
    }

    return () => {
      document.removeEventListener("filter-swiper-js-children", listener, false)
    }
  }, [swiperId]);
}

const useFilteredChildren = ({node, options, currentFilter}) => {
  const filteredChildren = node.children.filter(child => !currentFilter?.filter || currentFilter?.filter(child)) || []
  const reactChildren = domToReact(filteredChildren, options)
  let children =  reactChildren
  if (!Array.isArray(children)) {
    children = [children]
  }
  return children.filter(child => typeof child !== "string" || child.trim().replaceAll("\n", "") !== "")
}

const useBreakpoints = ({breakpoints}) => {
  return useMemo(() => {
    const rawBreakpoints = breakpoints
    if (!rawBreakpoints) {
      return undefined
    }

    try {
      return JSON5.parse(rawBreakpoints)
    } catch (e) {
      console.error("Invalid JSON in breakpoints attribute", e)
      return undefined
    }
  }, [breakpoints])
}

const useSetCurrentSwiperGlobalVariable = ({swiperId, currentSwiper}) => {
  useEffect(() => {
    if (!window["swiperJsInstances"] ) {
      window["swiperJsInstances"] = {}
    }

    window["swiperJsInstances"][swiperId] = currentSwiper

    return () => {
      delete window["swiperJsInstances"][swiperId]
    }
  }, [currentSwiper, swiperId])
}

const useOnSwiperInitialized = ({swiperId, currentSwiper}) => {
  useEffect(() => {
    if (!currentSwiper) {
      return
    }
    const event = new CustomEvent("swiper-js-initialized", { detail: {
        id: swiperId,
        swiper: currentSwiper
      }
    })
    document.dispatchEvent(event)

    // You can listen to events like this:
    // const listener = (e) => { ... }
    // document.addEventListener("swiper-js-initialized", listener, false)

  }, [swiperId, currentSwiper]);
}

export const RenderedSwiper = ({node, options}) => {
  const [currentFilter, setCurrentFilter] = React.useState(null)
  const [currentSwiper, setCurrentSwiper] = React.useState(null)

  useSwiperFilterListener({swiperId: node.attribs.id, setCurrentFilter})
  const children = useFilteredChildren({node, options, currentFilter})
  const breakpoints = useBreakpoints({breakpoints: node.attribs['data-breakpoints']})

  useSetCurrentSwiperGlobalVariable({swiperId: node.attribs.id, currentSwiper})
  useOnSwiperInitialized({swiperId: node.attribs.id, currentSwiper})

  const getSetting = (dataAttribute, convert) => {
    return node.attribs[dataAttribute] ? convert(node.attribs[dataAttribute]) : undefined
  }

  return <>
    <Swiper
      id={node.attribs.id}
      onInit={(swiper) => {
        setCurrentSwiper(swiper)
      }}
      freeMode={{draggable: getSetting('data-free-mode', (x) => x === 'true')}}
      speed={getSetting('data-speed', parseInt)}
      spaceBetween={getSetting('data-space-between', parseInt)}
      slidesPerView={getSetting('data-slides-per-view', parseInt)}
      loop={getSetting('data-loop', (x) => x === 'true')}
      navigation={getSetting('data-navigation', (x) => x === 'true')}
      autoplay={{
        delay: getSetting('data-autoplay-delay', parseInt),
        disableOnInteraction: getSetting('data-autoplay-disable-on-interaction', (x) => x === 'true')
      }}
      pagination={{clickable: getSetting('data-pagination-clickable', (x) => x === 'true')}}
      scrollbar={{draggable: getSetting('data-scrollbar-draggable', (x) => x === 'true')}}
      modules={[Autoplay, Pagination, Navigation]}
      breakpoints={breakpoints}
    >

      {children.map(child => {
        return <SwiperSlide>{child}</SwiperSlide>
      })}
    </Swiper>
  </>
}